/**
 * anchorBbox — Compute a tight highlight bbox by matching a citation
 * anchor phrase against a page's text_spans.
 *
 * Why this exists:
 *   ChunkHighlight renders one rectangle from chunk.bbox.  Chunks span
 *   whole paragraphs (often 500-900 chars covering many visual lines),
 *   so the rectangle covers way more than the sentence the LLM actually
 *   quoted.  Citations carry an anchor_start phrase (~30 chars from the
 *   start of the relevant sentence); intersecting that against the
 *   page's text_spans (one per visual line, normalized 0-1 coords)
 *   gives us a tight, sentence-level highlight at zero extra storage
 *   cost — text_spans are already loaded for the in-doc Ctrl+F path.
 *
 * Matching strategy:
 *   1. Normalise both haystack (concatenated span text) and needle
 *      (anchor_start) — collapse whitespace, lowercase.  This handles
 *      PDF text-layer quirks where word joiners and double spaces
 *      sneak in.
 *   2. Find the first span whose normalised text starts the anchor.
 *   3. Walk forward extending the bbox until anchor_end is reached,
 *      or we've covered ~3 visual lines (a sentence rarely exceeds
 *      that on a PIL page).
 *   4. Return the union as a tight bbox.  ``null`` on miss → caller
 *      falls back to chunk.bbox, no regression.
 */
import { diffChars } from 'diff';

import type { TextSpan, ChunkBbox } from '../../types/viewer';

/** The Alphabetic Presentation Forms a PDF font may substitute for letters. */
const LIGATURES: Record<string, string> = {
  'ﬀ': 'ff', 'ﬁ': 'fi', 'ﬂ': 'fl',
  'ﬃ': 'ffi', 'ﬄ': 'ffl', 'ﬅ': 'st', 'ﬆ': 'st',
};

/** Exported for the threshold-tuning harness, which must build its haystack
 *  exactly the way this module does rather than re-implementing the fold. */
export function normalise(s: string): string {
  return s
    // Markdown emphasis is OURS, not the document's.
    //
    // Cited sentences come from stored chunk text, which carries the markers a
    // parser wrote — measured on staging, a KEYTRUDA review sentence arrived as
    // "**Blinding**: Open-label ...". The PDF's text layer has no asterisks, so
    // the needle could never be found on the page it plainly sits on, and the
    // citation silently drew nothing. The single retry below does not help: it
    // strips only LEADING non-alphanumerics, and these markers are mid-string.
    //
    // Applied to BOTH the needle and the page's spans, so the transform is
    // symmetric and the char-range bookkeeping that maps a match back to its
    // boxes still lines up. Deliberately ONLY emphasis markers — parentheses,
    // percent signs and hyphens are data in a regulatory sentence, and
    // over-normalising the haystack is how a needle starts matching text it
    // does not belong to.
    .replace(/[*_`]/g, '')
    // A TABLE PIPE is the same kind of marker, with one difference: it
    // SEPARATES rather than decorates, so it folds to a space instead of
    // nothing. Deleting it welds two cells into `aminotransferase|ast` ->
    // `aminotransferaseast`, a word printed on no page; the PDF lays the cells
    // out with space between them.
    //
    // Measured on staging 2026-10-07 over 557 pages / 15 documents: of the
    // cited sentences the exact matcher could not locate, 41% carried a pipe,
    // and removing it alone turned 44 of them into exact matches. Review
    // documents are mostly tables, so this is the largest single cause of a
    // citation drawing no box — and unlike approximate matching it costs
    // nothing in precision.
    //
    // The existing leading-junk retry below already handled a needle that
    // STARTS with a pipe; this is for the ones that carry it mid-sentence,
    // which that retry cannot reach.
    .replace(/\|/g, ' ')
    // Typographic punctuation is the mirror of the markdown case above: the
    // PDF's characters that our stored text does not have.
    //
    // Measured on staging 2026-10-05 (KEYTRUDA review, doc 15674): chunk
    // 2062414 stores "Applicant's" with U+0027 while page 33's text layer has
    // U+2019 — the same sentence, the same page, unfindable over one quote
    // mark. And chunk 2062752 stores the SAME sentence with U+2019, so this is
    // a coin flip taken per chunk, not one bad document.
    //
    // Quotes, dashes and non-breaking spaces only. They are rendering choices
    // and no regulatory meaning turns on which one a typesetter picked.
    // Parentheses, braces, percent signs and digits are left alone — over-
    // normalising the haystack is how a needle starts matching text it does not
    // belong to, which is worse than drawing nothing.
    .replace(/[\u2018\u2019\u201A\u201B\u2032]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F\u2033]/g, '"')
    .replace(/[\u2010-\u2015\u2212]/g, '-')
    .replace(/[\u00A0\u2007\u202F]/g, ' ')
    // A soft hyphen is a typesetter's "you may break the word here" mark. It
    // prints as nothing when the word does not wrap, so one side carrying it
    // and the other not is a difference about layout, never about words.
    .replace(/\u00AD/g, '')
    // Ligatures: one glyph, several letters. PyMuPDF hands back whichever the
    // font used, so the page can read `de\uFB01nition` where the chunk says
    // `definition`.
    .replace(/[\uFB00-\uFB06]/g, (m) => LIGATURES[m] ?? m)
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}


interface SpanRange { idx: number; from: number; to: number }
/** One located region of the haystack. A LIST of these, not one, because a
 *  carton prints the same statement on two panels and both are the cited
 *  text — see `allOccurrences`. */
interface Hit { at: number; until: number }

/**
 * Union the bboxes of every span overlapping the matched character range.
 *
 * Shared by the exact and the word-level paths so a match found either way is
 * turned into a rectangle the same way — the two cannot drift apart.
 */
/** Normalised page coords are 0-1; EPS absorbs float rounding at the edges,
 *  which is a different thing from a coordinate wrong by a sixth of a page. */
const PAGE_EPS = 0.01;
const outOfPage = (v: number) => v < -PAGE_EPS || v > 1 + PAGE_EPS;
const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

/**
 * One rect per covered line, trimmed to the matched characters.
 *
 * Iterates the RANGES rather than the span index span [first..last]. The old
 * union walked every index in between, which silently swept in any span whose
 * text had normalised away to nothing and any span the match merely straddled.
 * A range exists only for a span that contributed text to the haystack, so a
 * sentence that wraps past an empty or furniture span no longer claims it.
 */
function rectsOf(
  textSpans: TextSpan[],
  ranges: SpanRange[],
  at: number,
  until: number,
): ChunkBbox[] {
  const out: ChunkBbox[] = [];
  for (const r of ranges) {
    if (r.to <= at || r.from >= until) continue;
    const s = textSpans[r.idx];
    if (!s) continue;
    if (!isFinite(s.x0) || !isFinite(s.y0) || !isFinite(s.x1) || !isFinite(s.y1)) continue;
    // Per-line validity: a lying span costs its own line and nothing else.
    // Under the union this same check had to reject the whole highlight.
    if (outOfPage(s.x0) || outOfPage(s.y0) || outOfPage(s.x1) || outOfPage(s.y1)) continue;
    if (s.x1 <= s.x0 || s.y1 <= s.y0) continue;

    // Trim the first and last covered lines to the characters actually cited.
    // Middle lines are covered end to end, so they keep their full width.
    const len = r.to - r.from;
    if (len <= 0) continue;
    const cFrom = Math.max(0, at - r.from);
    const cTo = Math.min(len, until - r.from);
    if (cTo <= cFrom) continue;

    const w = s.x1 - s.x0;
    const x0 = s.x0 + (w * cFrom) / len;
    const x1 = s.x0 + (w * cTo) / len;
    if (x1 <= x0) continue;

    out.push({ x0: clamp01(x0), y0: clamp01(s.y0), x1: clamp01(x1), y1: clamp01(s.y1) });
  }
  return mergeAlongLine(out);
}

/**
 * Join rects that sit on the same line with only a word gap between them.
 *
 * Span granularity is not a constant and cannot be assumed either way: measured
 * 10.2 words/span on INFLECTRA doc 1978 and 6.4 on XARACOLL doc 4917, while
 * other corpora in this codebase are genuinely per-word. Emitting one rect per
 * SPAN would therefore paint a 20-word sentence as 20 separate strokes with
 * hairline gaps at every space — a zebra, not a highlight.
 *
 * Merging on geometry rather than on span count makes the output depend on the
 * page's LAYOUT instead of on how the extractor happened to chop it up: one
 * stroke per line, whether that line arrived as one span or twenty.
 *
 * The two thresholds are deliberately tight. A line-height tolerance that is
 * too loose would weld a wrapped sentence into one tall block — the union bug
 * returning by the back door — and a gap tolerance that is too loose would
 * jump a two-column page, bridging text that was never cited.
 */
const SAME_LINE_Y = 0.004;   // ~0.4% of page height: a baseline wobble, not a new line
const WORD_GAP_X = 0.02;     // ~2% of page width: a space, not a column gutter

/**
 * Add `r` to `kept` unless it is visually redundant, and return whether it was
 * added.
 *
 * Exact-equality dedupe was enough while a rect covered whole spans: two probes
 * ending inside the same word produced the identical box. Sub-line trimming
 * broke that — "…for Gr" and "…for Grade" now stop at different x inside the
 * word `Grade`, so the keys differ by a few characters' width while the strokes
 * sit one on top of the other and double the fill opacity into a colour nobody
 * chose. That is the exact failure the dedupe exists to prevent, so the rule has
 * to be CONTAINMENT rather than equality.
 *
 * Both directions matter, because arrival order is the anchors' order, not
 * longest-first: a contained newcomer is dropped, and a newcomer that swallows
 * something already kept replaces it.
 */
export function keepDistinctRect(kept: ChunkBbox[], r: ChunkBbox): boolean {
  const EPS = 1e-6;
  const onSameLine = (k: ChunkBbox) =>
    Math.abs(k.y0 - r.y0) <= SAME_LINE_Y && Math.abs(k.y1 - r.y1) <= SAME_LINE_Y;

  for (let i = 0; i < kept.length; i++) {
    const k = kept[i]!;
    if (!onSameLine(k)) continue;
    if (r.x0 >= k.x0 - EPS && r.x1 <= k.x1 + EPS) return false;        // inside
    if (k.x0 >= r.x0 - EPS && k.x1 <= r.x1 + EPS) { kept[i] = r; return true; } // swallows
  }
  kept.push(r);
  return true;
}

function mergeAlongLine(rects: ChunkBbox[]): ChunkBbox[] {
  if (rects.length <= 1) return rects;
  const out: ChunkBbox[] = [];
  for (const r of rects) {
    const prev = out[out.length - 1];
    const sameLine = prev
      && Math.abs(prev.y0 - r.y0) <= SAME_LINE_Y
      && Math.abs(prev.y1 - r.y1) <= SAME_LINE_Y;
    // Ordered left-to-right within a line by construction (ranges follow the
    // haystack), so only the forward gap needs checking.
    const adjacent = prev && r.x0 - prev.x1 <= WORD_GAP_X;
    if (prev && sameLine && adjacent) {
      prev.x0 = Math.min(prev.x0, r.x0);
      prev.x1 = Math.max(prev.x1, r.x1);
      prev.y0 = Math.min(prev.y0, r.y0);
      prev.y1 = Math.max(prev.y1, r.y1);
      continue;
    }
    out.push({ ...r });
  }
  return out;
}


/** Minimum share of the cited sentence's words that must line up. */
export const FUZZY_MIN_SCORE = 0.8;

/** A non-overlapping runner-up this close to the best match is a coin flip. */
const FUZZY_TIE_MARGIN = 0.05;

/** Shortest sentence we will locate approximately. Below this, too little
 *  distinguishes one candidate from another to risk a wrong box. */
const FUZZY_MIN_WORDS = 5;

interface Tok { text: string; from: number; to: number }

function tokenise(s: string): Tok[] {
  const out: Tok[] = [];
  const re = /\S+/g;
  for (let m = re.exec(s); m !== null; m = re.exec(s)) {
    out.push({ text: m[0], from: m.index, to: m.index + m[0].length });
  }
  return out;
}

/** Levenshtein, abandoned as soon as it exceeds ``max``. */
function withinEdits(a: string, b: string, max: number): boolean {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > max) return false;
  let prev = new Array<number>(b.length + 1);
  let cur = new Array<number>(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    cur[0] = i;
    let best = cur[0];
    for (let j = 1; j <= b.length; j++) {
      const sub = prev[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1);
      cur[j] = Math.min(sub, prev[j]! + 1, cur[j - 1]! + 1);
      if (cur[j]! < best) best = cur[j]!;
    }
    if (best > max) return false;   // no cell on this row can still win
    const swap = prev; prev = cur; cur = swap;
  }
  return prev[b.length]! <= max;
}

/**
 * DIGITS ARE NEVER FUZZY.
 *
 * A number is the one part of a regulatory sentence whose exact value is the
 * point. `median OS was 12.4 months` and `median OS was 12.1 months` differ by
 * one character out of sixty — any percentage threshold accepts the swap, and
 * the reader then trusts a box drawn over the wrong survival figure. OCR
 * mangles letters far more often than digits, so requiring an exact match on
 * any token carrying one costs almost nothing and removes the whole class.
 */
function tokensMatch(need: string, found: string): boolean {
  if (need === found) return true;
  if (/\d/.test(need) || /\d/.test(found)) return false;
  // One edit per four characters, so `confidential`/`confidentia` and
  // `nda/bla`/`nda:bla` pass while two unrelated short words do not.
  const budget = Math.floor(Math.min(need.length, found.length) / 4);
  if (budget < 1) return false;
  return withinEdits(need, found, budget);
}

/**
 * Locate ``needle`` in ``hay`` approximately, by WORDS rather than characters.
 *
 * Both strings must already be normalised. Returns the character range running
 * from the first matched word to the last, which is what the span-union below
 * needs; ``null`` whenever the match is too weak OR too ambiguous to trust.
 *
 * Word-level rather than a sliding character window for two reasons: OCR errors
 * sit INSIDE words (`Confidentia`), which word comparison absorbs naturally,
 * and a word boundary maps cleanly back to the page's word boxes while a fuzzy
 * character offset does not.
 *
 * Cost is bounded by the page: ~400 words of haystack against ~10 of needle, a
 * few thousand short string comparisons, run once per citation.
 */
export function fuzzyFindRange(
  hay: string,
  needle: string,
  minScore: number,
): { start: number; end: number; rival: { start: number; end: number } | null } | null {
  const need = tokenise(needle);
  const hayToks = tokenise(hay);
  if (need.length < FUZZY_MIN_WORDS || hayToks.length === 0) return null;

  // Allow the page to carry words the stored sentence does not (and vice
  // versa) — OCR splits and joins tokens — but not unboundedly, or the match
  // stops being one sentence.
  const windowCap = need.length * 2 + 4;

  const cands: { start: number; end: number; score: number }[] = [];
  for (let i = 0; i < hayToks.length; i++) {
    let matched = 0;
    let h = i;
    let firstTok = -1;
    let lastTok = -1;
    let digitMissed = false;
    for (let n = 0; n < need.length && h < hayToks.length && h - i < windowCap; n++) {
      // Walk the page forward looking for this word, but only a little way:
      // skipping far ahead would stitch words from different sentences.
      const lookahead = Math.min(h + 3, hayToks.length);
      let hit = -1;
      for (let k = h; k < lookahead; k++) {
        if (tokensMatch(need[n]!.text, hayToks[k]!.text)) { hit = k; break; }
      }
      if (hit < 0) {
        // An ABSENT WORD is merely a cost to the score — except a word carrying
        // a digit, which is a hard refusal. Scoring it as a cost is what lets
        // `12.4 months` match `12.1 months`: ten of eleven words still line up,
        // and 0.91 clears any sane threshold. The number not being on the page
        // means this is a different sentence, whatever else agrees.
        if (/\d/.test(need[n]!.text)) { digitMissed = true; break; }
        continue;
      }
      matched++;
      if (firstTok < 0) firstTok = hit;
      lastTok = hit;
      h = hit + 1;
    }
    if (digitMissed || firstTok < 0) continue;
    const score = matched / need.length;
    if (score < minScore) continue;
    cands.push({ start: hayToks[firstTok]!.from, end: hayToks[lastTok]!.to, score });
  }
  if (cands.length === 0) return null;

  // Best score first; among equals the earliest, which is insertion order.
  //
  // A tightest-range tiebreak was tried and REVERTED: measured over 291
  // known-correct pages it moved nothing (63 edge-differing boxes before and
  // after), so it was complexity justified only by argument.
  cands.sort((a, b) => b.score - a.score);
  const best = cands[0]!;

  // AMBIGUITY REFUSAL, mirroring the exact path's tie rule. Candidates starting
  // a word apart describe the same place, so only a runner-up that does NOT
  // overlap the winner counts as a competing answer.
  const rival = cands.find((c) => c.end <= best.start || c.start >= best.end);
  if (rival && rival.score >= best.score - FUZZY_TIE_MARGIN) return null;
  // A RIVAL STATING DIFFERENT NUMBERS IS NOT SETTLED BY A LETTER SCORE.
  //
  // FOUND BY AUDIT. The score counts matched WORDS; the thing in dispute here
  // is a NUMBER. A page printing "...2.4% among..." and "...3.42% among...",
  // where the cited 3.42% line carries one more OCR typo, scores the clean
  // 2.4% line higher by more than FUZZY_TIE_MARGIN — so the tie rule above
  // does not fire and the box lands on the wrong figure.
  //
  // The rival already had to clear FUZZY_MIN_SCORE to be here. If it also
  // states different numbers, the two readings are not separable by anything
  // this function measures, whatever the margin. Same principle as
  // `pickOccurrence`'s twin rule: distance and letter-similarity are evidence
  // about WHERE text is, never about WHICH number was cited.
  if (rival && !sameNumbers(
    hay.slice(best.start, best.end), hay.slice(rival.start, rival.end),
  )) return null;

  // The runner-up is handed back so the CALLER can compare whole sentences.
  // The disputed number usually sits between the head and the tail, which is
  // outside every region this function scores — so the twin check cannot live
  // here, only the evidence for it.
  return {
    start: best.start,
    end: best.end,
    rival: rival ? { start: rival.start, end: rival.end } : null,
  };
}


/** Enough of the sentence's words to LOCALISE a region. Deliberately far
 *  below FUZZY_MIN_SCORE because this score no longer decides acceptance —
 *  `ALIGN_MIN_COVERAGE` does. Its only job is to narrow a 1.5-4k character
 *  page down to one candidate window, so that `diffChars` runs over ~200
 *  characters instead of the whole page. */
const ALIGN_MIN_SCORE = 0.45;

/** Share of the cited sentence's CHARACTERS that must align before we accept.
 *  Measured on staging: of the sentences this pass exists to recover,
 *  gemini-tier aligns at a median 94.2% and 69.0% reach 0.80, while the
 *  native-tier misses align at a median 2.2% — those are not on the page at
 *  all (a wrong stored `page_num`) and this floor is what keeps them out. */
const ALIGN_MIN_COVERAGE = 0.8;

/**
 * Locate ``needle`` by ALIGNING its characters, for the sentences the word
 * pass drops.
 *
 * WHY A FOURTH PASS EXISTS. Measured on staging over 2,259 real cited
 * sentences run through this module: wrong boxes are 1 in 788 verifiable
 * cases and ZERO of those came from `fuzzyFindRange`. There is no precision
 * left to win. What is left is MISSES — 6.1% of sentences on native pages and
 * 29.8% on gemini-tier pages draw nothing — and on the OCR pages the sentence
 * is printed right there: character alignment of the same sentence against the
 * same page covers a median 94.2% of it.
 *
 * WHY THE WORD PASS CANNOT REACH THEM. `tokensMatch` gives each word an edit
 * budget of `floor(min(len)/4)`, so a word of three characters or fewer must
 * match EXACTLY — `budget < 1` returns false. A scanned page reading `thc` for
 * `the` or `ol` for `of` therefore hard-fails those tokens, and enough of them
 * drags the score under FUZZY_MIN_SCORE even though the sentence is plainly
 * present. Characters have no such cliff: three single-character substitutions
 * in a sixty-character sentence is 95% aligned.
 *
 * WHAT KEEPS IT FROM BEING "JUST LOOSER". It does not choose the region. It
 * localises through `fuzzyFindRange`, so FUZZY_MIN_WORDS, the
 * digits-are-never-fuzzy rule and the tie refusal all still decide which
 * window is even considered; the rival is handed back so the caller's twin
 * check still runs; and the caller still applies `acceptable()`, so a
 * disagreeing number is still refused. The alignment only answers one
 * question: is the region the word pass already found close enough at the
 * character level to accept?
 *
 * REACHED ONLY when exact, squeeze and fuzzy have all found nothing, so every
 * citation that boxes correctly today takes an earlier branch and is untouched
 * by this.
 */
function alignFindRange(
  hay: string,
  needle: string,
): { start: number; end: number; rival: { start: number; end: number } | null } | null {
  const cand = fuzzyFindRange(hay, needle, ALIGN_MIN_SCORE);
  if (!cand) return null;

  // ALIGN AGAINST THE CANDIDATE REGION ONLY — never a widened window.
  //
  // An earlier version padded the window by 30% of the needle on each side, so
  // that a sentence whose opening word was the mis-scanned one was not clipped.
  // That padding reached into the NEIGHBOURING LINE, and a character diff
  // matches characters wherever it can: on a two-line fixture it aligned the
  // needle's `th` to `this` and its `e ` to `page ` one line above, so the
  // range began there and the stroke was drawn at y 0.20 for a sentence
  // printed at y 0.40.
  //
  // Clustering the aligned runs did not save it either — the coincidental hits
  // sat within a few characters of the real match, which is exactly the gap a
  // genuine page-side insertion also produces, so no gap threshold separates
  // them. The window itself was the bug. `fuzzyFindRange` has already vetted
  // this region at the word level, so aligning inside it and nowhere else
  // removes the whole failure mode rather than filtering its symptoms.
  //
  // What a fixed pad got wrong was being unconditional. The candidate already
  // spans the sentence whenever its first and last words matched, and padding
  // THAT is pure risk. So pad only by the amount the candidate falls SHORT of
  // the sentence's own length — the shortfall is exactly the mis-scanned edge
  // words — and by nothing at all when it does not fall short.
  //
  // Measured on the staging fixtures: with no padding the candidate's coverage
  // runs at a median 65.5% and only 1.8% reach ALIGN_MIN_COVERAGE, because the
  // characters of the clipped edge words are not in the window to align
  // against. The floor was not wrong; the window was.
  const slack = Math.max(0, needle.length - (cand.end - cand.start));
  const from = Math.max(0, cand.start - slack);
  const window = hay.slice(from, Math.min(hay.length, cand.end + slack));

  const al = alignSpan(needle, window);
  if (!al) return null;
  const { first, last, equal } = al;
  if (equal / needle.length < ALIGN_MIN_COVERAGE) return null;

  // The rival travels with the result: the caller compares whole sentences to
  // settle twins, and a region found by alignment is no less likely to have a
  // twin than one found by words.
  return { start: from + first, end: from + last, rival: cand.rival };
}


/**
 * Every place `needle` is printed verbatim, as hits that clear the position
 * guard.
 *
 * `positionOk` still applies to each copy independently: two copies of a needle
 * that is a prefix of a longer number are two wrong boxes, not two right ones,
 * so `the approved dose was 15` against a page printing `150 mg` twice refuses
 * both rather than drawing both.
 */
function allOccurrences(hay: string, needle: string): Hit[] {
  if (needle.length < SQUEEZE_MIN_CHARS) return [];
  const out: Hit[] = [];
  for (let i = hay.indexOf(needle); i >= 0; i = hay.indexOf(needle, i + 1)) {
    const until = i + needle.length;
    if (positionOk(hay, { at: i, headEnd: until, tail: null, until })) {
      out.push({ at: i, until });
    }
  }
  return out;
}


/**
 * THE FIFTH PASS: compare whole SENTENCES at character level over windows the
 * page's own line breaks define, and let the argmax pick.
 *
 * WHY THE OTHER FOUR CANNOT REACH THESE. They all localise before they score.
 * `fuzzyFindRange` anchors on word TOKENS, so OCR that runs words together
 * (`applicantrcported`) or splits them leaves almost nothing to align: measured
 * on staging, 68.6% of gemini-tier misses are `no_candidate_even_at_0.45`, the
 * floor the alignment pass itself localises at. There is no region to score, so
 * character comparison never happens.
 *
 * A line boundary costs nothing to find and needs no search. That is the whole
 * idea: you do not look for the window, you already have it.
 *
 * MEASURED as a fallback behind all four, over 2,259 real cited sentences and
 * their real page spans:
 *
 *     native no-box   6.1%  ->  1.2%      (50 of 64 misses now draw)
 *     gemini no-box  28.6%  -> 19.1%      (115 of 347)
 *     ~5 ms per citation click
 *
 * It does NOT remove the ceiling. For the gemini misses it still cannot reach,
 * the best window on the whole page has a median similarity of 0.512 — the
 * sentence is not legibly on the page, and that is an ingestion problem.
 *
 * COST is kept at that 5 ms by scoring in two stages: character-trigram Dice is
 * O(n+m) and cheap enough to run on every window, and only the best twelve pay
 * for a real edit distance. Scoring every window exhaustively with Levenshtein
 * is ~100x that and buys nothing the shortlist loses.
 */
const SENT_MIN_SCORE = 0.65;
/** Within this much of the best score, a rival is "near-tied" and the number
 *  tie-break may prefer it. */
const SENT_TIE_WINDOW = 0.10;
/** Stop growing a window past this multiple of the sentence's length. A window
 *  much longer than the needle cannot score well anyway — the extra characters
 *  are all edits — so growing further only costs time. */
const SENT_MAX_SPAN = 1.6;
/** How many windows earn a real edit distance. */
const SENT_SHORTLIST = 12;
/** Shortest sentence this pass will act on. Below this, a line window is long
 *  enough relative to the needle that 0.65 stops being evidence of anything. */
const SENT_MIN_CHARS = 24;

/**
 * Longest sentence a caller should hand this pass, and the cap every producer
 * of that needle must apply.
 *
 * Measured over 2,259 real cited sentences: median 93 characters, p95 285,
 * p99 451, max 1,051. 400 covers 98% and bounds what a citation URL has to
 * carry when a click opens a new tab, where several cited spans ride one param.
 *
 * Truncation is self-consistent rather than lossy: the window search sizes
 * itself relative to the needle it is given, so a needle cut to 400 matches the
 * best 400-character window of the same sentence.
 *
 * LIVES HERE, exported, because three separate writers produce this needle —
 * `MessageBubble`, `DrugDetailPage.seedChunkAnchor` and `citeAnchorTriples` —
 * and they disagreed: two clamped to a magic 400 and the URL one did not clamp
 * at all, so one citation highlighted differently depending on whether it
 * opened in this tab or a new one. Found by whiteboard-defense.
 */
export const ANCHOR_TEXT_MAX = 400;

/**
 * Where inside `window` the `needle`'s characters actually sit, and how many of
 * them aligned.
 *
 * `removed` is needle text absent from the page; `added` is page text the needle
 * does not have and must still be stepped over, or every later offset is wrong.
 *
 * Shared by the alignment pass (which uses `equal` to decide acceptance) and the
 * sentence pass (which uses `first`/`last` to TRIM its window down to the cited
 * characters). The sentence pass needs this for a reason that is not about
 * matching at all — see `sentenceFindRange`.
 */
function alignSpan(
  needle: string,
  window: string,
): { first: number; last: number; equal: number } | null {
  let equal = 0;
  let first = -1;
  let last = -1;
  let pos = 0;
  for (const part of diffChars(needle, window)) {
    if (part.removed) continue;
    if (part.added) { pos += part.value.length; continue; }
    if (first < 0) first = pos;
    equal += part.value.length;
    pos += part.value.length;
    last = pos;
  }
  return first < 0 ? null : { first, last, equal };
}

/** Character trigram multiset. */
function trigrams(s: string): Map<string, number> {
  const m = new Map<string, number>();
  for (let i = 0; i + 3 <= s.length; i++) {
    const g = s.slice(i, i + 3);
    m.set(g, (m.get(g) ?? 0) + 1);
  }
  return m;
}

/** Dice coefficient over trigram multisets: cheap, and close enough to rank. */
function diceOf(a: Map<string, number>, na: number, b: Map<string, number>, nb: number): number {
  if (!na || !nb) return 0;
  let inter = 0;
  for (const [g, c] of a) {
    const d = b.get(g);
    if (d) inter += Math.min(c, d);
  }
  return (2 * inter) / (na + nb);
}

/** Levenshtein similarity, `1 - distance / max(len)`. Two rows, so the memory
 *  is O(min) rather than O(n*m) — these strings reach a few hundred characters
 *  and this runs twelve times per citation. */
function levRatio(a: string, b: string): number {
  if (a === b) return 1;
  if (!a.length || !b.length) return 0;
  let prev = new Array<number>(b.length + 1);
  let cur = new Array<number>(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    cur[0] = i;
    const ca = a.charCodeAt(i - 1);
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(
        prev[j]! + 1,
        cur[j - 1]! + 1,
        prev[j - 1]! + (ca === b.charCodeAt(j - 1) ? 0 : 1),
      );
    }
    const swap = prev; prev = cur; cur = swap;
  }
  return 1 - prev[b.length]! / Math.max(a.length, b.length);
}

/** The numbers in a string, in order, as the comparison key for the
 *  tie-break. Mirrors `sameNumbers`' notion of a number. */
function numberKey(s: string): string {
  return (s.match(/\d+(?:[.,]\d+)*/g) ?? []).join('|');
}

/**
 * Where the page's printed LINES start and end inside the haystack.
 *
 * Derived from span geometry, not from the text: a new line begins wherever a
 * span's vertical centre departs from the previous span's by more than
 * `SAME_LINE_Y`, the same tolerance the span-union step already uses. Spans
 * are walked in array order so every window stays CONTIGUOUS in `hay`, which
 * is what lets the result hand `rectsOf` plain character offsets and inherit
 * its per-line trimming unchanged.
 */
function lineBoundsOf(textSpans: TextSpan[], ranges: SpanRange[]): { from: number; to: number }[] {
  const out: { from: number; to: number }[] = [];
  let prevMid: number | null = null;
  for (const r of ranges) {
    const s = textSpans[r.idx]!;
    const mid = (s.y0 + s.y1) / 2;
    if (prevMid === null || Math.abs(mid - prevMid) > SAME_LINE_Y) {
      out.push({ from: r.from, to: r.to });
    } else {
      out[out.length - 1]!.to = r.to;
    }
    prevMid = mid;
  }
  return out;
}

function sentenceFindRange(
  hay: string,
  sentence: string,
  lineBounds: { from: number; to: number }[],
): { start: number; end: number } | null {
  const needle = normalise(sentence);
  if (needle.length < SENT_MIN_CHARS || lineBounds.length === 0) return null;

  const nt = trigrams(needle);
  let ntn = 0;
  for (const [, c] of nt) ntn += c;
  if (ntn === 0) return null;

  const limit = needle.length * SENT_MAX_SPAN;
  const shortlist: { dice: number; from: number; to: number }[] = [];
  for (let i = 0; i < lineBounds.length; i++) {
    for (let j = i; j < lineBounds.length; j++) {
      const from = lineBounds[i]!.from;
      const to = lineBounds[j]!.to;
      // Always score the single starting line, however long it is, so a page
      // whose lines all exceed the limit is not skipped entirely.
      if (to - from > limit && j > i) break;
      const text = hay.slice(from, to);
      const wt = trigrams(text);
      let wtn = 0;
      for (const [, c] of wt) wtn += c;
      shortlist.push({ dice: diceOf(nt, ntn, wt, wtn), from, to });
      if (to - from >= limit) break;
    }
  }
  if (shortlist.length === 0) return null;

  shortlist.sort((a, b) => b.dice - a.dice);
  const scored = shortlist.slice(0, SENT_SHORTLIST).map((c) => ({
    ...c,
    score: levRatio(needle, hay.slice(c.from, c.to)),
  })).sort((a, b) => b.score - a.score);

  const best = scored[0]!;
  if (best.score < SENT_MIN_SCORE) return null;

  // THE NUMBER TIE-BREAK, and why it is a tie-break rather than a gate.
  //
  // `acceptable()` requires the matched region's numbers to equal the needle's.
  // That rule earns its place on the other passes, which match a 60-character
  // ANCHOR: `Dose 1` fits the 1.5 mg row and the 15 mg row equally and nothing
  // downstream can separate them. Over a whole sentence they separate
  // themselves, because the decoy is one character worse and loses the argmax —
  // measured directly, in 29 cases where the needle's digits named one window
  // and a different-location window carried other digits, the argmax took the
  // right row 29 times and the decoy 0.
  //
  // Meanwhile the gate was the single most expensive rule in the matcher:
  // enforcing it here cut gemini recovery from 115 to 38 of 347 while leaving
  // the error rate alone (2.9% -> 3.1%), because the 246 draws it refused were
  // not decoys. They were OCR mangling the digits of the one true row that IS
  // printed there — which is the entire reason this pass exists.
  //
  // n = 29 is small, so the cheap half of the rule stays: among windows the
  // argmax cannot confidently separate, prefer one whose numbers actually
  // agree. It costs one comparison per shortlisted window and covers the case
  // that sample is too small to rule out.
  const key = numberKey(needle);
  const tied = key ? scored.filter((c) => c.score >= best.score - SENT_TIE_WINDOW) : [];
  const agreeing = key
    ? tied.find((c) => numberKey(hay.slice(c.from, c.to)) === key)
    : undefined;
  const pick = agreeing ?? best;

  // TRIM THE WINDOW TO THE CITED CHARACTERS, which is not about matching.
  //
  // A window is whole LINES, so returning it raw makes `at` the first line's
  // start and `until` the last line's end. `rectsOf` then computes cFrom = 0 and
  // cTo = len for every range it covers, its per-line trimming cannot fire, and
  // the result is a full-WIDTH stroke on each line — with SENT_MAX_SPAN at 1.6,
  // up to a third of those characters were never cited.
  //
  // That breaks a DIFFERENT feature. `keepDistinctRect` dedupes by CONTAINMENT
  // precisely because rects are trimmed to the cited text, so containment means
  // redundancy. An untrimmed full-width band contains a sibling sentence's tight
  // rect without being redundant with it, and `PageRenderer`'s dedupe then
  // evicts the sibling — deleting a box the matcher got RIGHT, or dropping this
  // one, depending on anchor order. Either way a line is painted end to end in
  // one sentence's colour, asserting words the answer never cited.
  //
  // So align the needle inside the winning window and keep only the span its
  // characters actually occupy. Found by whiteboard-defense; every test here was
  // single-anchor, so nothing caught it.
  const text = hay.slice(pick.from, pick.to);
  const al = alignSpan(needle, text);
  const start = al ? pick.from + al.first : pick.from;
  const end = al ? pick.from + al.last : pick.to;

  // THIS PASS NEEDS ITS OWN NUMERIC POSITION GUARD, and the reason is a chain.
  //
  // `positionOk` protects the other paths from boxing a needle that is a PREFIX
  // of a longer number — a citation saying 15 landing inside a page's 150. But
  // it only runs when an EARLIER pass located something. When every earlier
  // pass finds nothing at all, `fuzzyBranch` returns a plain null rather than
  // 'stop', no position check has run, and this pass is free to make exactly
  // that mistake by itself: measured on a page reading "thc mean exposure at
  // stcady state was 150 mg daily", a cited "...was 15" scores 0.76 and draws.
  //
  // The trimming above makes it sharper, not safer. An untrimmed window ends at
  // a line end, where the next character is a space, so `endsMidNumber` could
  // never fire; a trimmed range ends wherever the needle's characters stop,
  // which for "…was 15" is precisely between the 5 and the 0 of 150. So the
  // guard the trim earns is the same guard the trim makes reachable.
  //
  // WORD position is deliberately NOT checked. Tolerating mangled letters is
  // this pass's entire purpose, and `startsMidToken` would refuse the OCR
  // damage it exists to absorb. Numbers are the opposite: they are what
  // diverges between the two extractions, and a wrong dose is the one error
  // this system must never make quietly.
  if (startsMidNumber(hay, start)) return null;
  if (endsMidNumber(hay, end)) return null;
  return { start, end };
}


/**
 * The cited phrase as ONE RECT PER COVERED LINE, trimmed to the matched
 * characters — a highlighter stroke, not a box around the paragraph.
 *
 * WHY THIS REPLACES THE UNION BOX. `locateAnchor` already knows exactly which
 * spans the sentence covers and where it starts and ends inside each one. The
 * old code computed that and then collapsed it to min/max on all four sides,
 * throwing the per-line geometry away. A sentence spanning three lines became
 * a single rectangle over all three at full width — including the indent, the
 * ragged right edge, and whatever sat between the lines.
 *
 * Two consequences, both of which the reader saw:
 *
 *  - CONSECUTIVE SENTENCES MERGED. Five cited sentences in one paragraph drew
 *    five overlapping blocks that read as one undifferentiated wash, with no
 *    way to tell which words carried which claim.
 *  - ONE BAD SPAN POISONED EVERYTHING. Measured on XARACOLL doc 4917 page 7,
 *    34 of 39 spans store y outside the normalised 0-1 range. A union
 *    inherits the worst coordinate on every side, so the box covered the whole
 *    page; the guard added for it then had to refuse the ENTIRE highlight.
 *    Per-line, a lying span costs exactly its own line and the rest still
 *    draws.
 *
 * SUB-LINE PRECISION. Spans here are lines, not words — measured 10.2
 * words/span on INFLECTRA doc 1978 and 6.4 on XARACOLL doc 4917, so a rect
 * covering a whole span would still over-claim at both ends of the sentence.
 * The first and last covered spans are therefore trimmed proportionally by
 * character offset within the span's own text. That is an approximation for a
 * proportional font — a 'W' is wider than an 'i' — but it is bounded by the
 * line it sits on and errs by a fraction of a line, where the union errs by a
 * paragraph.
 */
export function computeAnchorRects(
  textSpans: TextSpan[] | undefined,
  anchorStart: string | undefined | null,
  anchorEnd?: string | null,
  sentence?: string | null,
): ChunkBbox[] {
  const found = locateAnchor(textSpans, anchorStart, anchorEnd, sentence);
  if (!found || !textSpans) return [];
  // One set of rects per located copy. `keepDistinctRect` in the caller
  // already collapses rects that land on the same region, so two copies on one
  // line cannot double the overlay opacity.
  return found.hits.flatMap((h) => rectsOf(textSpans, found.ranges, h.at, h.until));
}

/**
 * Try to compute a tight bbox covering the anchor phrase.
 *
 * Kept as the UNION of `computeAnchorRects`, because two callers legitimately
 * want one rectangle: the scroll/landing calculation, which needs a single y
 * to centre on, and tests that assert a match was found at all.
 *
 * @param textSpans  Page text spans (line-level, normalised 0-1 coords)
 * @param anchorStart  First ~30 chars of the quoted sentence from
 *                     citation.anchor_start.  Required.
 * @param anchorEnd    Last ~30 chars of the quoted sentence.  Optional;
 *                     when present we walk forward until we cover it.
 *
 * Returns the union bbox in normalised page coords (same shape as
 * ChunkBbox so PageRenderer can pass it to ChunkHighlight unchanged).
 * Returns ``null`` when no match is found.
 */
export function computeAnchorBbox(
  textSpans: TextSpan[] | undefined,
  anchorStart: string | undefined | null,
  anchorEnd?: string | null,
  sentence?: string | null,
): ChunkBbox | null {
  const rects = computeAnchorRects(textSpans, anchorStart, anchorEnd, sentence);
  if (rects.length === 0) return null;
  // Union of the per-line rects, NOT of the raw spans: a line whose stored
  // geometry is broken has already been dropped, so it can no longer drag the
  // union off the page the way it used to.
  const x0 = Math.min(...rects.map((r) => r.x0));
  const y0 = Math.min(...rects.map((r) => r.y0));
  const x1 = Math.max(...rects.map((r) => r.x1));
  const y1 = Math.max(...rects.map((r) => r.y1));
  return { x0, y0, x1, y1 };
}

/** Shortest squeezed needle we will accept. With spaces removed a short
 *  needle can straddle a word boundary — `therate` sits inside `the rate` —
 *  so there has to be a floor. Cited sentences run 30-60 characters, well
 *  clear of it; this only excludes fragments too small to be evidence. */
const SQUEEZE_MIN_CHARS = 16;

/**
 * Every NUMBER in a piece of text, as a token.
 *
 * THIS REPLACES THREE ATTEMPTS AT THE SAME GUARD, each of which shipped and
 * each of which an audit broke with a wrong-DOSE highlight:
 *
 *   1. squeeze every separator      -> cited "1.5 mg" boxed a page's "15 mg"
 *   2. keep `.` between two digits  -> page spans ['1','.','5'] join as
 *                                      "1 . 5", space on both sides, dropped,
 *                                      so "15 mg" boxed "1.5 mg" again
 *   3. look past the spaces         -> a dropped character between the digit
 *                                      and the dot ("5\u2020 . 10", '"5". 10')
 *                                      smuggled the digit back in, so cited
 *                                      "5.10 mg" boxed a page printing 5 and
 *                                      10 as separate numbers
 *
 * All three asked "is this dot a decimal point?" from the characters around
 * it. That question is not answerable: a page ending a line with "5." before
 * one starting "10 mg" is indistinguishable, character by character, from a
 * split "5.10". The adjacency heuristic was the wrong idea, not an idea with
 * three bugs in it, so it is gone rather than patched a fourth time.
 *
 * What replaces it asks the question that IS answerable, and the one
 * `fuzzyFindRange` already asked correctly one function away: do the two sides
 * contain THE SAME NUMBERS? A token is a maximal run of digits and separators,
 * with separators trimmed off the end so a sentence-final period does not
 * change the number. The squeeze is then free to drop every separator, because
 * the numbers are compared before a match is accepted — and compared on the
 * ORIGINAL text, where the separators are still there.
 */
const NUM_TOKEN = /[.,-]?[0-9][0-9.,-]*/g;

function numbersOf(s: string): string[] {
  // The LEADING separator is part of the token. Without it ".5" and "5" both
  // read as "5", and a citation about 0.5 mg boxed a page printing 5 mg — a
  // tenfold dose error that the fourth audit found on the exact path. A
  // leading "-" counts too: "-5" is not "5". That costs the `phase-3` against
  // `phase 3` kind of match, which is the correct side of the trade.
  //
  // The TRAILING separator is not: a sentence-final period does not change a
  // number.
  return (s.match(NUM_TOKEN) ?? []).map((t) => t.replace(/[.,-]+$/, ''));
}

/** Where a match sits, in haystack coordinates: the head's range, the tail's
 *  range when one was found, and the end of the whole span to box. */
interface Located {
  at: number;
  headEnd: number;
  tail: { start: number; end: number } | null;
  until: number;
}

/**
 * THE POST-CONDITION, applied wherever a match is accepted.
 *
 * Four audits found a wrong-DOSE highlight in this module, and the fourth
 * found three more AFTER the first three were fixed — every one of them on an
 * acceptance point the guard had not been wired into:
 *
 *   - the EXACT pass had no number comparison at all, so a cited ".5 mg" boxed
 *     a page printing "5 mg" and vice versa;
 *   - the TAIL was located with a bare `indexOf` and never checked, so an
 *     `anchor_end` of "5 mg daily" (the last ~30 characters of the sentence,
 *     which can begin mid-number) boxed a line reading "15 mg daily";
 *   - the FUZZY pass skipped page tokens inside its lookahead gap whatever
 *     they said, so a cited "5 mg once daily" boxed "5 10 mg once daily".
 *
 * Guarding one path at a time is what produced four rounds of this. Every path
 * still goes through the same two checks — but through them SEPARATELY, as
 * `positionOk` and `numbersOk`, because the two halves must be answered
 * differently once a fifth pass exists to fall through to. The single
 * `acceptable()` wrapper that used to stand here was deleted rather than kept
 * for convenience: a caller reaching for it would silently re-conflate exactly
 * what `positionOk` explains must stay apart.
 *
 * The head and the tail are checked SEPARATELY and against their own slices,
 * because `anchor_start` and `anchor_end` are a prefix and a suffix of the
 * cited sentence — the middle is not available here, so the numbers between
 * them cannot be compared and must not be.
 */

/**
 * THE POSITION HALF, and it is TERMINAL ON EVERY PATH.
 *
 * These three checks all say the same thing: the match landed INSIDE a word or
 * a number, so the needle is a PREFIX of something the page actually prints.
 * A cited "…starting dose was 15" located inside "150 mg" is not a near miss to
 * be recovered — the page does not say 15.
 *
 * SPLIT OUT because the sentence pass must never be offered one of these.
 * Hooking the fifth pass onto an `acceptable()` refusal routed BOTH halves to
 * it, and the position half is precisely what it cannot re-decide: handed the
 * same region it scores the same line at 0.68 and boxes `2150 mg` for a
 * citation saying `150 mg`. Reproduced on a page reading "patients received
 * 2150 mg of drug x once daily for twelve weeks" against a cited
 * "150mg of drug x once daily for twelve weeks", which the squeeze pass
 * matches (the stored text is missing the space the page prints) and then
 * refuses on `startsMidToken`.
 *
 * The exact path used to carry a special case saying exactly this in prose.
 * With the halves separated the special case is gone: position refusals end
 * every path, number refusals fall through on every path, and no branch has to
 * remember which it is. Found by whiteboard-defense; the 2,259-sentence corpus
 * cannot see it, because its ground truth excludes digits by construction.
 */
function positionOk(hay: string, o: Located): boolean {
  if (startsMidToken(hay, o.at)) return false;
  if (endsMidNumber(hay, o.headEnd)) return false;
  if (o.tail) {
    // mid-NUMBER, not mid-word: see `startsMidNumber`.
    if (startsMidNumber(hay, o.tail.start)) return false;
    if (endsMidNumber(hay, o.tail.end)) return false;
  }
  return true;
}

/**
 * THE NUMBER HALF, and this one MAY fall through to the sentence pass.
 *
 * Measured: 246 of 849 gemini-tier draws die here, and they are not decoys —
 * they are OCR mangling the digits of the one row that IS printed. Over a whole
 * sentence the argmax separates a real decoy by itself (29 of 29 measured), so
 * the sentence pass is allowed to re-decide this and only this.
 */
function numbersOk(
  hay: string,
  needle: string,
  needleEnd: string | null,
  o: Located,
): boolean {
  if (!sameNumbers(needle, hay.slice(widenLeft(hay, o.at), o.headEnd))) return false;
  if (o.tail && needleEnd) {
    if (!sameNumbers(
      needleEnd, hay.slice(widenLeft(hay, o.tail.start), o.tail.end),
    )) return false;
  }
  // NO THIRD CHECK ON `o.until`, and that is reasoned, not forgotten. It is
  // always one of the two positions already checked: with no tail it equals
  // `headEnd`; with one it is `tail.end`, except in the fuzzy path where it is
  // `max(headEnd, tail.end)` and so still one of them. A mutation removing a
  // check here failed nothing, which is what sent me to look — a guard no
  // input can reach is not a guard.
  return true;
}

/**
 * A match must not BEGIN inside a word or a number.
 *
 * Found by audit on the exact path, where it predates the squeeze: a stored
 * anchor of ".5 mg once daily" has its leading junk trimmed to "5 mg once
 * daily" by the retry in `locateAnchor`, which then matches the `5` INSIDE a
 * page's `15`. One occurrence, so nothing refused it, and the box landed on a
 * sentence about a tenfold different dose. No length floor helps — the
 * position is wrong, not the length.
 *
 * Applied to BOTH passes, because both are substring searches and a substring
 * search against a page has no word boundaries of its own.
 */
function startsMidToken(hay: string, at: number): boolean {
  return /[a-z0-9]/i.test(hay[at - 1] ?? '') && /[a-z0-9]/i.test(hay[at] ?? '');
}

/**
 * ...and must not END inside a NUMBER: a match stopping inside a page's `450`
 * yields the same `45` the needle holds. Digits only. Letters are left alone
 * because `anchor_start` is a ~30-character prefix of the cited sentence and
 * may legitimately stop mid-word.
 */
function endsMidNumber(hay: string, until: number): boolean {
  return /[0-9]/.test(hay[until - 1] ?? '') && /[0-9]/.test(hay[until] ?? '');
}

/**
 * The TAIL may begin mid-WORD — it is a suffix of the cited sentence, so
 * `anchor_end` of "…ed by the analytical data." starting inside "supported" is
 * normal and five existing tests depend on it. It must not begin mid-NUMBER,
 * which is the whole of the fourth audit's tail finding: a tail of "5 mg
 * daily" starting at the `5` inside a page's `15`.
 */
function startsMidNumber(hay: string, at: number): boolean {
  return /[0-9]/.test(hay[at - 1] ?? '') && /[0-9]/.test(hay[at] ?? '');
}

/**
 * Widen a slice start by one so a number beginning with a separator is not cut
 * in half by the slice boundary.
 *
 * Without it a tail of "5 mg daily" compared against a page's ".5 mg daily"
 * slices from the `5`, reads "5", and agrees with itself — the separator that
 * makes the two numbers different sits one character outside the window.
 */
function widenLeft(hay: string, at: number): number {
  return /[0-9]/.test(hay[at] ?? '') && /[.,-]/.test(hay[at - 1] ?? '') ? at - 1 : at;
}

/** Do two pieces of text state the same numbers, in the same order? */
function sameNumbers(a: string, b: string): boolean {
  const x = numbersOf(a);
  const y = numbersOf(b);
  return x.length === y.length && x.every((n, i) => n === y[i]);
}

/** Drop punctuation AND spaces — the class this serves is a missing space, so
 *  keeping spaces fixes nothing (see the call site).
 *
 *  KEPT: letters, digits, brackets and the percent sign. The brackets and `%`
 *  are kept because `normalise()` already refuses to fold them for a reason
 *  that survives here unchanged — they carry meaning in a regulatory sentence,
 *  and `{95% CI: 0.49, 0.88}` matching `(95% CI: 0.49, 0.88)` is a different
 *  claim about the document, not a typesetting choice. Pinned by
 *  anchorBbox.typography.test.ts, which this pass must not loosen.
 *
 *  Keeping the brackets costs real recall and the cost is known: measured on
 *  staging 2026-10-11, 1.2% of all sentences would match if they were squeezed
 *  too and do not match because they are not. Paying that to keep a box off
 *  text the citation does not name is the right direction.
 *
 *  Number separators are dropped here and guarded by `sameNumbers` at the call
 *  site instead. Three versions of this function tried to decide which dots
 *  were decimal points from their neighbours; see `numbersOf`.
 *
 *  ONE implementation, and `squeezeText` is defined in terms of it. They were
 *  two hand-copied character classes held together by a comment — an audit
 *  noted nothing asserted they agreed, and if they drift a match maps to the
 *  wrong offsets and boxes the wrong words. */
function squeezeText(s: string): string {
  return squeeze(s).text;
}

/** The haystack squeezed, plus the index back. `map[i]` is the position in
 *  `hay` of the i-th surviving character, so a match at `[p, q)` in the
 *  squeezed text is `[map[p], map[q - 1] + 1)` in haystack coordinates — which
 *  is the space `ranges` and `rectsOf` already work in, so nothing downstream
 *  has to know this pass happened. */
function squeeze(hay: string): { text: string; map: number[] } {
  let text = '';
  const map: number[] = [];
  for (let i = 0; i < hay.length; i++) {
    const ch = hay[i]!;
    if (/[a-z0-9()[\]{}%]/i.test(ch)) {
      text += ch;
      map.push(i);
    }
  }
  return { text, map };
}

/**
 * Choose WHICH printing of the needle is the cited one, in whatever coordinate
 * space the caller hands over — the normalised haystack, or the squeezed one.
 *
 * Extracted so the exact and the punctuation-insensitive passes cannot drift
 * apart: the duplicate-printing refusal and the nearest-tail tie-break are the
 * two rules that keep a box off the wrong text, and having one copy of them is
 * the only way both passes are guaranteed to apply them.
 */
/** `'none'` means the needle is not printed here; `'refused'` means it IS and
 *  nothing on the page says WHICH printing is cited. The two must not collapse
 *  into one null — an audit caught exactly that: the extraction turned a
 *  refusal into "not found", which then fell through to the squeeze pass and
 *  on to the fuzzy path, whose tie rule is a margin rather than an equality.
 *  The old inline code returned null from `locateAnchor` outright. A refusal is
 *  a decision, not a failure to find. */
type Occurrence = Located | 'none' | 'refused';

function pickOccurrence(
  hay: string,
  needle: string,
  needleEnd: string | null,
): Occurrence {
  // EVERY place the head is printed, not just the first.
  //
  // Taking `indexOf` and never reconsidering put the box on whichever printing
  // came first, and — because the tail was then searched forward from there —
  // stretched the union across everything between the two. Measured on staging
  // 2026-10-02 against real cited spans: 2.8%-15.3% of them have a repeated
  // head, and nearly all of those are resolved by the tail we already hold.
  const starts: number[] = [];
  for (let i = hay.indexOf(needle); i >= 0; i = hay.indexOf(needle, i + 1)) {
    starts.push(i);
  }
  if (starts.length === 0) return 'none';

  // Score each occurrence by how closely its own tail follows it. The cited
  // sentence is the one whose end is right there; a coincidental printing of
  // the same opening words has the tail far away, or not after it at all.
  const scored = needleEnd
    ? starts
        .map((s) => {
          const e = hay.indexOf(needleEnd, s);
          return e < 0 ? null : {
            start: s, tailStart: e, end: e + needleEnd.length, gap: e - s,
          };
        })
        .filter((x): x is {
          start: number; tailStart: number; end: number; gap: number;
        } => x !== null)
    : [];

  // NO DISTANCE CAP, and that is a measured decision rather than an omission.
  //
  // A head whose tail sits very far away would union a runaway region. Measured
  // on staging 2026-10-02: that is 2 cases in ~2,200. Of the far-apart matches
  // generally, only 38 of 848 are a genuine column or page wrap where both ends
  // are correct and the span between them is NOT cited. The rest are ordinary
  // long downward flow, where the wide box is right.
  //
  // DISCOUNT THE APPROVAL-LETTER SHARE of that 38: 30 of them are letters,
  // whose page geometry is independently known to be wrong (bad chunk page_num,
  // a sizeable fraction with no bbox at all) and whose parser work is deferred.
  // Wraps measured there are an artifact of that, not evidence about wrapping.
  // Excluding letters the rate is 8 of 609 — 1.3%, and all eight are UK PIL;
  // sba, epar, eu, ca_pm and au have zero between them.
  //
  // So a cap would truncate mostly-correct boxes to spare a 0.1% case, and
  // multi-region rendering would serve ~1% of that 0.1%. Choosing the
  // nearest-tail occurrence below already removes the common failure. The
  // trigger to revisit is a family OTHER than approval_letter showing real
  // wraps — re-measure once the letter parser is fixed, since that corpus
  // cannot answer the question until then.
  if (scored.length > 0) {
    scored.sort((a, b) => a.gap - b.gap);
    // A TIE means two printings are equally plausible and nothing on the page
    // distinguishes them. Refuse, exactly as `citation_span_resolve._bucket_for`
    // refuses a colliding id: a missing highlight says less than a wrong one.
    if (scored.length > 1 && scored[0]!.gap === scored[1]!.gap) return 'refused';
    // THE MIDDLE IS NOT AVAILABLE, SO IT CANNOT BE USED TO CHOOSE.
    //
    // `anchor_start` is a ~30-character prefix and `anchor_end` a ~30-character
    // suffix of the cited sentence; what sits between them was never sent. When
    // a page prints two sentences sharing BOTH ends, head-and-tail evidence
    // cannot say which was cited — and boxing one asserts a number the citation
    // never gave us.
    //
    // The gap rule above only catches that when the two middles are the same
    // LENGTH, which is an accident rather than a guard. Measured against the
    // shipped code: page "...was 2.4% among..." and "...was 3.42% among...",
    // citation the 3.42% sentence, box drawn on the 2.4% line. Dose-response
    // rows and multi-arm safety tables are exactly this shape.
    //
    // So: if the candidates state different NUMBERS in their middles, refuse.
    // Distance to the tail is not evidence about which sentence was cited, and
    // picking the nearer one is a coin flip wearing a box.
    //
    // Candidates whose middles agree numerically are still chosen by distance:
    // whichever is boxed shows the reader the same figures.
    //
    // CAVEAT, stated rather than hidden: in the squeeze pass this function runs
    // on separator-stripped text, so a middle of "2.4" reads as "24" there. It
    // still separates "2.4" from "3.42" (24 vs 342) and "7" from "11.5" (7 vs
    // 115), which is the realistic table shape, but it would not separate "2.4"
    // from "24". The head and tail numbers are compared on the ORIGINAL text by
    // `acceptable`, so this residual is confined to the middle of a twin
    // sentence reached through the squeeze pass.
    const best = scored[0]!;
    // ONLY GENUINELY COMPETING CANDIDATES COUNT AS TWINS.
    //
    // REGRESSION FOUND BY AUDIT on the first version of this rule. `indexOf`
    // searches the tail FORWARD from each head, so a head repeated in a table
    // of contents — "Study drug was given ........ 14" — pairs with the BODY
    // sentence's tail far below. Its "middle" then spans unrelated text full
    // of digits, the middles differ, and a citation that used to resolve
    // correctly drew nothing.
    //
    // That candidate is not a rival PRINTING of the sentence: its region
    // strictly CONTAINS the real one. Two printings of the same sentence have
    // disjoint regions. `best` has the smallest gap, so it cannot contain
    // another; dropping the candidates that contain IT leaves exactly the
    // genuine rivals. No threshold, no distance cap.
    const rivals = scored.filter(
      (c) => c === best || !(c.start <= best.start && c.end >= best.end),
    );
    if (rivals.length > 1) {
      const middles = rivals.map((c) => numbersOf(
        hay.slice(c.start + needle.length, c.tailStart),
      ).join('\u0000'));
      if (middles.some((m) => m !== middles[0])) return 'refused';
    }
    return {
      at: best.start,
      headEnd: best.start + needle.length,
      tail: { start: best.tailStart, end: best.end },
      until: best.end,
    };
  }
  // No tail to choose with — either none was supplied, or it is not on this
  // page (a sentence continuing overleaf). One occurrence is still a true
  // location and gets boxed; several are a coin flip, so refuse.
  if (starts.length > 1) return 'refused';
  const only = starts[0]!;
  return {
    at: only, headEnd: only + needle.length, tail: null,
    until: only + needle.length,
  };
}

/**
 * Find WHERE the anchor phrase sits: the span ranges it covers and the exact
 * character offsets it starts and ends at inside the concatenated haystack.
 *
 * This is the whole matcher — typographic folding, the leading-junk retry, the
 * duplicate-occurrence tie-break, the digit rule and the fuzzy fallback. It
 * deliberately returns a LOCATION rather than a rectangle so that rendering is
 * a separate decision: `computeAnchorRects` draws one rect per line and
 * `computeAnchorBbox` unions them, and neither can change what counts as a
 * match. Before the split, "where is it" and "what shape do we draw" were the
 * same function, which is how a correct match kept producing a wrong rectangle.
 */
function locateAnchor(
  textSpans: TextSpan[] | undefined,
  anchorStart: string | undefined | null,
  anchorEnd?: string | null,
  sentence?: string | null,
): { ranges: SpanRange[]; hits: Hit[] } | null {
  if (!textSpans || textSpans.length === 0) return null;
  if (!anchorStart) return null;

  const needleStart = normalise(anchorStart);
  if (needleStart.length < 4) return null;  // too short to be reliable

  // Match ACROSS spans, not within one.
  //
  // The original implementation looked for a single span containing the whole
  // anchor phrase. That works only where a span is a visual LINE — true for UK
  // PIL, which is what it was written against. It is false for most corpora:
  // measured on prod, SBA and FDA approval-letter spans are per-WORD (median 1
  // word/span), EPAR 2 and EU 3. No single word can contain a 30-60 character
  // phrase, so on those corpora this returned null every time and the caller
  // silently fell back to the paragraph-sized chunk rectangle — the box that
  // was wrong often enough to get highlighting switched off.
  //
  // So concatenate the spans into one haystack, remember where each span lands
  // in it, find the phrase there, and union the spans it actually covers.
  // Line-level spans still work: they are just a haystack with fewer, longer
  // pieces.
  const ranges: { idx: number; from: number; to: number }[] = [];
  let hay = '';
  for (let i = 0; i < textSpans.length; i++) {
    const t = normalise(textSpans[i]!.text);
    if (!t) continue;
    if (hay.length > 0) hay += ' ';
    ranges.push({ idx: i, from: hay.length, to: hay.length + t.length });
    hay += t;
  }
  if (!hay) return null;

  // Leading junk is common and fatal to an exact match: bullet glyphs, list
  // markers and replacement characters survive into the extracted chunk text
  // but not into the page's text layer, so a needle beginning
  // "� The types and severity of..." can never be found even though the
  // sentence is plainly on the page. Retry once from the first alphanumeric
  // character before giving up.
  //
  // Only the FRONT is trimmed, and only once. Searching for ever-shorter
  // fragments would eventually match something unrelated, and a box over the
  // wrong text is worse than no box at all.
  let needle = needleStart;
  if (hay.indexOf(needle) < 0) {
    const trimmed = needleStart.replace(/^[^a-z0-9]+/i, '');
    if (trimmed.length >= 8 && trimmed !== needleStart) {
      needle = trimmed;
    }
  }

  // EVERY place the head is printed, not just the first.
  //
  // Taking `indexOf` and never reconsidering put the box on whichever printing
  // came first, and — because the tail was then searched forward from there —
  // stretched the union across everything between the two. Measured on staging
  // 2026-10-02 against real cited spans: 2.8%-15.3% of them have a repeated
  // head, and nearly all of those are resolved by the tail we already hold.
  const needleEnd = anchorEnd ? normalise(anchorEnd) : null;

  /**
   * THE FIFTH PASS, as a fallback the strict passes can hand off to.
   *
   * TWO KINDS OF REFUSAL, and only one of them may fall through here.
   *
   *  - An AMBIGUITY refusal (`pickOccurrence` returning 'refused') means the
   *    page prints the sentence twice and nothing distinguishes the copies.
   *    That is terminal, and the squeeze pass says so in its own words below:
   *    letting a looser rule answer what a stricter one just declined makes
   *    the looser rule the final word. Whole-sentence scoring cannot help —
   *    two identical windows score identically, so it would only guess.
   *
   *  - A POST-CONDITION refusal (`acceptable` returning false) is different.
   *    It means a region WAS located and then thrown away, overwhelmingly by
   *    the number rule: measured, 246 of 849 gemini draws die here. Those are
   *    not ambiguous, they are OCR mangling the digits of the one row that is
   *    printed, and recovering them is most of what this pass is for.
   *
   * Lazy on purpose — it costs nothing on the paths that never call it, which
   * is every citation that boxes correctly today.
   */
  const sentencePass = (): { ranges: SpanRange[]; hits: Hit[] } | null => {
    if (!sentence) return null;
    const found = sentenceFindRange(hay, sentence, lineBoundsOf(textSpans, ranges));
    return found ? { ranges, hits: [{ at: found.start, until: found.end }] } : null;
  };

  const exact = pickOccurrence(hay, needle, needleEnd);
  if (exact === 'refused') {
    // TEXT PRINTED TWICE IS NOT A COIN FLIP — IT IS TWO ANSWERS.
    //
    // `pickOccurrence` refuses here because the cited OPENING appears more
    // than once and the tail could not separate the copies. With a 60-char
    // anchor that was all it could do, and it is the single biggest cause of
    // missed native citations: 27 of 54, read off the matcher's own exit
    // points rather than inferred.
    //
    // The pages are carton and label artwork. A carton prints
    // `For Subcutaneous Use Only.` on the front panel AND a side panel, so
    // both copies are genuinely the cited sentence. Measured over the 48
    // refusals: 38 have the WHOLE sentence at two or more places, 2 have it at
    // exactly one, and only 8 repeat the opening alone.
    //
    // So ask about the whole sentence rather than its opening, and draw every
    // place it is printed. Disambiguating instead would be wrong twice over:
    // neither copy is incorrect, and the chunk bbox that looked like the
    // tie-breaker separates 0 of 48 — it is stored in PDF points while spans
    // are normalised, and once converted it spans the whole page, because on
    // these pages the chunk IS the page.
    //
    // Only decidable because the whole sentence now reaches the matcher. With
    // the anchor alone, "printed twice" and "two different passages sharing an
    // opening" are the same input.
    const copies = sentence ? allOccurrences(hay, normalise(sentence)) : [];
    return copies.length > 0 ? { ranges, hits: copies } : null;
  }
  if (exact !== 'none') {
    // EITHER refusal is terminal on this path, and that is not the asymmetry it
    // looks like. An exact match means the needle appears character for
    // character, so the numbers IN it appear in the page too — the only way
    // `numbersOk` can fail here is `widenLeft` pulling in a separator sitting
    // just outside the match, which is the page printing `.5 mg` where the
    // citation says `5 mg`. That is a tenfold dose difference, not OCR damage,
    // so no looser pass may re-decide it. The number fallthrough exists for
    // mangled digits, and digits cannot be mangled in a verbatim match.
    if (!positionOk(hay, exact)) return null;
    if (!numbersOk(hay, needle, needleEnd, exact)) return null;
    return { ranges, hits: [{ at: exact.at, until: exact.until }] };
  }

  // NOTHING MATCHED EXACTLY — IGNORE PUNCTUATION AND SPACING AND TRY AGAIN.
  //
  // The largest fixable class of missing highlights. Measured on staging
  // 2026-10-11 over ~1,340 sentences from 500 chunks across 495 documents:
  // exact matching resolves 53.3%, and a large share of the rest agree with
  // the page on every LETTER AND DIGIT and differ only in punctuation or a
  // space. `"2.Adequate"` stored against `"2. Adequate"` printed (XARACOLL
  // chunk 957963) is the shape.
  //
  // MEASURED EFFECT of this pass, same sample, same rule as shipped including
  // the length floor and the duplicate refusal: resolution 53.3% -> 62.8%,
  // i.e. +9.5 points, 127 of 626 misses. One case in the sample was refused as
  // a duplicate printing rather than guessed, which is the intended behaviour.
  // The remaining 36% miss for reasons no matching rule reaches: 12% have half
  // the words absent, 10% have the words in a different order, 5% are not on
  // that page at all. Those are the two-extractions-disagree classes, and the
  // fix for them is persisting the parser's own offset->bbox map at ingest,
  // not a cleverer search here.
  //
  // `normalise()` cannot absorb this, and that is not an oversight. The
  // divergence is a MISSING SPACE, not a character substitution: fold the
  // punctuation away and keep the space and you have `"2adequate"` against
  // `"2 adequate"`, still nothing. Space-insensitivity needs characters
  // REMOVED, which destroys the per-span offsets `rectsOf` maps a match back
  // through — so it cannot be a symmetric per-span string rewrite the way
  // every fold in `normalise()` is. Hence a separate pass that carries an
  // index back to haystack coordinates.
  //
  // Precision is unchanged in the three ways that matter. Digits survive the
  // squeeze, so a dose mismatch still cannot match — the rule
  // `fuzzyFindRange` enforces explicitly holds here by construction. It is
  // still a SUBSTRING search, so words merely present in a different order do
  // not match. And `pickOccurrence` is the same function the exact path uses,
  // so the duplicate-printing refusal and the nearest-tail tie-break apply
  // identically.
  //
  // What it does cost: with spaces gone a needle can straddle a word boundary
  // (`therate` inside `the rate`). `SQUEEZE_MIN_CHARS` is the floor that stops
  // it; cited sentences are 30-60 characters, so the class this serves is
  // nowhere near it.
  //
  // Ordered BEFORE the fuzzy path on purpose: this one knows the letters and
  // digits agree exactly, which is strictly stronger evidence than a 0.8 word
  // score, and it costs one linear scan.
  const sq = squeeze(hay);
  const sqNeedle = squeezeText(needle);
  if (sqNeedle.length >= SQUEEZE_MIN_CHARS) {
    const near = pickOccurrence(
      sq.text,
      sqNeedle,
      needleEnd ? squeezeText(needleEnd) : null,
    );
    // A refusal here ends it too. Two printings that differ only in
    // punctuation are as indistinguishable as two identical ones, and letting
    // the fuzzy path have a go at what this pass just declined would make the
    // looser rule the final word.
    if (near === 'refused') return null;
    if (near !== 'none') {
      // Map all four offsets back to haystack coordinates before validating:
      // the squeezed text has no separators, so the numbers can only be
      // compared on the original.
      const back = (k: number) => sq.map[k]!;
      const o: Located = {
        at: back(near.at),
        headEnd: back(near.headEnd - 1) + 1,
        tail: near.tail
          ? { start: back(near.tail.start), end: back(near.tail.end - 1) + 1 }
          : null,
        until: back(near.until - 1) + 1,
      };
      // Same split as the exact path above. This is the branch whiteboard-
      // defense found: the squeeze pass matches a needle missing the space the
      // page prints, lands INSIDE `2150`, and a blanket `acceptable()` refusal
      // handed that straight to the sentence pass.
      if (!positionOk(hay, o)) return null;
      if (!numbersOk(hay, needle, needleEnd, o)) return sentencePass();
      const { at, until } = o;
      return { ranges, hits: [{ at, until }] };
    }
  }

  // STILL NOTHING — fall back to locating the sentence by WORDS.
  //
  // A scanned page is read twice by two different engines: the chunk's text by
  // the Gemini OCR backfill, the word boxes by Tesseract. The two copies of one
  // page then disagree character by character (`NDA/BLA` vs `NDA:BLA`,
  // `Confidential` vs `Confidentia`), so an exact search finds nothing even
  // though the sentence is plainly printed. Measured on staging doc 15674: all
  // three citations that drew no box were on OCR'd pages; the one that worked
  // was native.
  //
  // Loose matching is defensible HERE and nowhere else, because the backend
  // already resolved which page — the haystack is ~1.5-4k characters, not a
  // corpus, so few candidates can compete. `fuzzyFindRange` keeps the exact
  // path's two safety rules: digits must match exactly, and a non-overlapping
  // runner-up of similar quality is refused rather than guessed.
  //
  // Reached ONLY when the exact search found nothing, so every citation that
  // boxes correctly today is untouched by this.
  // THE FUZZY BRANCH IS NOW A UNIT, so that a refusal anywhere inside it falls
  // through to the sentence pass rather than ending the match.
  //
  // This matters more than it looks. The expensive refusals are not "no region
  // found" — they are the POST-CONDITIONS firing on a region the word pass
  // located perfectly well: measured, 246 of 849 gemini draws are thrown away
  // by `acceptable()`'s number rule after a successful localisation. Hooking
  // the fifth pass on `!head` alone would have left every one of those
  // unreachable, which is most of what it was built to recover.
  const viaFuzzy = fuzzyBranch(hay, needle, needleEnd);
  if (viaFuzzy) return { ranges, hits: [{ at: viaFuzzy.at, until: viaFuzzy.until }] };

  // The fuzzy branch's own twin refusal DOES fall through, unlike the exact and
  // squeeze ones. It compares two candidate HEADS, which on a table row is
  // `Dose 1` against `Dose 1` — identical by construction, so it must refuse.
  // Whole sentences are not identical: the decoy is one character worse and
  // loses the argmax, which is the measured 29-of-29 result. So the ambiguity
  // that is real for an anchor is resolvable for a sentence.
  return sentencePass();
}

/**
 * Locate the sentence by WORDS, then by CHARACTERS inside the region words
 * found — the third and fourth passes, with their own post-conditions.
 *
 * Extracted verbatim from `locateAnchor` so that a refusal here is a FALLBACK
 * POINT rather than the end of the search; nothing about its rules changed.
 */
function fuzzyBranch(
  hay: string,
  needle: string,
  needleEnd: string | null,
): { at: number; until: number } | null {
  let at: number;
  let until: number;
  // Word scoring first. Only if it finds nothing do we align characters — the
  // order matters, because the word pass carries guarantees the alignment
  // cannot reconstruct (a whole-word match maps cleanly back to a word box).
  // See `alignFindRange` for why a fourth pass exists at all.
  const head = fuzzyFindRange(hay, needle, FUZZY_MIN_SCORE)
    ?? alignFindRange(hay, needle);
  if (!head) return null;
  at = head.start;
  until = head.end;
  let fuzzyTail: { start: number; end: number } | null = null;
  // Extend to the sentence's own tail when we hold one, so the box covers the
  // whole quoted sentence rather than just its opening words — the same shape
  // the exact path produces. Searched only AFTER the head, and a failure here
  // is not fatal: the head's box is already a true location.
  if (needleEnd) {
    const tail = fuzzyFindRange(hay.slice(at), needleEnd, FUZZY_MIN_SCORE);
    if (tail) {
      fuzzyTail = { start: at + tail.start, end: at + tail.end };
      until = Math.max(until, at + tail.end);
    }
  }
  // A RIVAL SENTENCE STATING DIFFERENT NUMBERS IS A TWIN, AND UNRESOLVABLE.
  //
  // FOUND BY AUDIT after the `pickOccurrence` twin rule shipped: the fuzzy
  // path never calls `pickOccurrence`, so it was untouched. A page printing
  // "...2.4% among..." and "...3.42% among...", where the CITED 3.42% line
  // carries one more OCR typo, scores the clean line higher by more than
  // FUZZY_TIE_MARGIN — the tie rule does not fire and the box lands on the
  // wrong figure.
  //
  // The comparison spans the WHOLE matched sentence, not the head: the
  // disputed number sits between head and tail, outside every region
  // `fuzzyFindRange` scores. The rival's window is taken at the same length,
  // which is sound because two printings of one sentence differ by a few
  // characters — that is what makes them twins.
  if (head.rival) {
    // NO TAIL MEANS NOTHING TO COMPARE WITH. When the head matches two places
    // and the tail could not be established — commonly because the twins share
    // it word for word, so its own tie rule refuses — the comparison window is
    // the head alone and the disputed number sits outside it. Two plausible
    // heads and no way to tell them apart is a coin flip, so refuse. The exact
    // path has always done this ("several occurrences are a coin flip"); fuzzy
    // was the looser one.
    if (!fuzzyTail) return null;
    const span = until - at;
    const rivalRegion = hay.slice(head.rival.start, head.rival.start + span);
    if (!sameNumbers(hay.slice(at, until), rivalRegion)) return null;
  }
  // The fuzzy path is the one that tolerates mangled LETTERS, which is exactly
  // why it must not tolerate mangled NUMBERS. It previously skipped page
  // tokens inside its lookahead gap whatever they said.
  //
  // EITHER refusal here falls through to the sentence pass, unlike the exact
  // and squeeze paths, and the reason is that this match is APPROXIMATE.
  //
  // A verbatim match landing inside `2150` proves the page prints 2150, so no
  // pass may box it. A fuzzy or aligned match landing inside a word proves only
  // that this pass's own localisation was poor — and the sentence pass does not
  // inherit that region at all: it re-searches from scratch over line-anchored
  // windows and carries its own numeric position guards. A 'stop' signal here
  // was a branch no test could reach, which the comment on `o.until` below
  // already warns is not a guard.
  const located: Located = { at, headEnd: head.end, tail: fuzzyTail, until };
  if (!positionOk(hay, located)) return null;
  if (!numbersOk(hay, needle, needleEnd, located)) return null;
  return { at, until };
}
